import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ReportsData } from "@/hooks/useReportsData";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import { format } from "date-fns";
import { BookOpenCheck, Target, TrendingUp, AlertTriangle } from "lucide-react";

interface QuizzesAnalyticsProps {
  data: ReportsData;
  compact?: boolean;
}

const DIFFICULTY_COLORS: Record<string, string> = {
  easy: "hsl(var(--chart-2))",
  medium: "hsl(var(--chart-3))",
  hard: "hsl(var(--chart-5))",
};

const QUESTION_TYPE_LABELS: Record<string, string> = {
  multiple_choice: "Multiple Choice",
  true_false: "True/False",
  free_response: "Free Response",
};

export const QuizzesAnalytics: React.FC<QuizzesAnalyticsProps> = ({
  data,
  compact = false,
}) => {
  const completedQuizzes = data.quizAttempts.filter(
    (a) => a.completed_at !== null
  );
  const totalAttempts = completedQuizzes.length;
  const avgScore =
    totalAttempts > 0
      ? Math.round(
          completedQuizzes.reduce((sum, a) => sum + (a.score || 0), 0) /
            totalAttempts
        )
      : 0;

  // Score trend data (last 10 attempts)
  const scoreTrendData = completedQuizzes
    .slice(0, 10)
    .reverse()
    .map((attempt, index) => {
      // Normalize score: if stored as 0-1 decimal, convert to 0-100
      const rawScore = attempt.score || 0;
      const normalizedScore = rawScore <= 1 && rawScore > 0 ? Math.round(rawScore * 100) : Math.round(rawScore);
      return {
        attempt: `#${index + 1}`,
        score: normalizedScore,
        date: attempt.completed_at
          ? format(new Date(attempt.completed_at), "MMM d")
          : "",
      };
    });

  // Difficulty breakdown data
  const difficultyData = data.quizDifficultyStats.map((stat) => ({
    name: stat.difficulty.charAt(0).toUpperCase() + stat.difficulty.slice(1),
    score: stat.avg_score,
    attempts: stat.attempt_count,
    fill: DIFFICULTY_COLORS[stat.difficulty] || "hsl(var(--chart-1))",
  }));

  const chartConfig = {
    score: { label: "Score", color: "hsl(var(--chart-1))" },
  };

  if (totalAttempts === 0) {
    return (
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <BookOpenCheck className="h-5 w-5" />
            Quizzes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex h-48 items-center justify-center text-center">
            <div>
              <BookOpenCheck className="mx-auto mb-3 h-12 w-12 text-muted-foreground/50" />
              <p className="text-muted-foreground">
                Complete your first quiz to see performance analytics.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (compact) {
    return (
      <Card className="border-border bg-card">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <BookOpenCheck className="h-5 w-5" />
            Quizzes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Quizzes Completed
              </span>
              <span className="font-medium text-foreground">{totalAttempts}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Avg Score</span>
              <span className="font-medium text-foreground">{avgScore}%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Accuracy</span>
              <span className="font-medium text-foreground">
                {data.overallQuizAccuracy}%
              </span>
            </div>

            {/* Mini difficulty bars */}
            <div className="pt-2">
              <p className="mb-2 text-xs text-muted-foreground">By Difficulty</p>
              {difficultyData.map((d) => (
                <div key={d.name} className="mb-1.5 flex items-center gap-2">
                  <span className="w-16 text-xs">{d.name}</span>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${d.score}%`,
                        backgroundColor: d.fill,
                      }}
                    />
                  </div>
                  <span className="text-xs text-muted-foreground">{d.score}%</span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <BookOpenCheck className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Quizzes Taken</p>
                <p className="text-xl font-semibold">{totalAttempts}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-info/10 p-2">
                <Target className="h-5 w-5 text-info" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Average Score</p>
                <p className="text-xl font-semibold">{avgScore}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-correct/10 p-2">
                <TrendingUp className="h-5 w-5 text-correct" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Overall Accuracy</p>
                <p className="text-xl font-semibold">{data.overallQuizAccuracy}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-orange/10 p-2">
                <AlertTriangle className="h-5 w-5 text-orange" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Weak Topics</p>
                <p className="text-xl font-semibold">{data.quizWeakTopics.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Score Trend */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-lg">Score Trend</CardTitle>
          </CardHeader>
          <CardContent>
            {scoreTrendData.length > 0 ? (
              <ChartContainer config={chartConfig} className="h-48 w-full">
                <AreaChart data={scoreTrendData}>
                  <XAxis
                    dataKey="attempt"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 12 }}
                    domain={[0, 100]}
                  />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Area
                    type="monotone"
                    dataKey="score"
                    stroke="hsl(var(--chart-1))"
                    fill="hsl(var(--chart-1))"
                    fillOpacity={0.2}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ChartContainer>
            ) : (
              <div className="flex h-48 items-center justify-center">
                <p className="text-sm text-muted-foreground">No data yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Difficulty Breakdown */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-lg">Performance by Difficulty</CardTitle>
          </CardHeader>
          <CardContent>
            {difficultyData.length > 0 ? (
              <ChartContainer config={chartConfig} className="h-48 w-full">
                <BarChart data={difficultyData} layout="vertical">
                  <XAxis type="number" domain={[0, 100]} tickLine={false} axisLine={false} />
                  <YAxis
                    dataKey="name"
                    type="category"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 12 }}
                    width={60}
                  />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar dataKey="score" radius={4} />
                </BarChart>
              </ChartContainer>
            ) : (
              <div className="flex h-48 items-center justify-center">
                <p className="text-sm text-muted-foreground">No data yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Question Type Performance */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="text-lg">Performance by Question Type</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-3">
            {data.quizQuestionTypeStats.map((stat) => (
              <div
                key={stat.question_type}
                className="rounded-lg border border-border bg-muted/30 p-4"
              >
                <p className="text-sm text-muted-foreground">
                  {QUESTION_TYPE_LABELS[stat.question_type] || stat.question_type}
                </p>
                <p className="mt-1 text-2xl font-semibold">{stat.accuracy}%</p>
                <p className="text-xs text-muted-foreground">
                  {stat.correct}/{stat.total} correct
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Weak Topics */}
      {data.quizWeakTopics.length > 0 && (
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <AlertTriangle className="h-5 w-5 text-orange" />
              Topics to Review
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.quizWeakTopics.map((topic) => (
                <div
                  key={topic.topic}
                  className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-3"
                >
                  <span className="font-medium">{topic.topic}</span>
                  <div className="flex items-center gap-3">
                    <div className="h-2 w-24 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-orange"
                        style={{ width: `${topic.accuracy}%` }}
                      />
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {topic.accuracy}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
